import type { IAuditEntry } from "@lexicanon/shared"
import { Trans } from "@lingui/react/macro"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import { fetchAuditLog } from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import { useTenant } from "../../lib/tenant"
import { AuditView } from "./-audit/AuditView"

function AuditLogPage(): React.JSX.Element {
  const [search, setSearch] = useState("")
  const { user } = useAuth()
  const { isAdmin: isAdminHost } = useTenant()
  // Super-admin on the admin host with no company selected: this page is the
  // platform audit, not a workspace's. Everyone else sees workspace copy.
  const isPlatform = isAdminHost && !!user?.isAdmin && !user.activeOrganizationId

  const {
    data: entries = [],
    isLoading,
    error,
    refetch,
  } = useCompanyQuery<IAuditEntry[]>({
    key: queryKeys.audit,
    queryFn: (company) => fetchAuditLog(company, 500),
  })

  const filtered = useMemo(() => {
    if (!search) return entries
    const q = search.toLowerCase()
    return entries.filter(
      (e) =>
        e.user_email.toLowerCase().includes(q) ||
        e.user_name.toLowerCase().includes(q) ||
        e.action.toLowerCase().includes(q) ||
        (e.meeting_subject || "").toLowerCase().includes(q) ||
        (e.details || "").toLowerCase().includes(q),
    )
  }, [entries, search])

  return (
    <AuditView
      entries={filtered}
      isLoading={isLoading}
      error={error ? error.message : null}
      search={search}
      onSearchChange={setSearch}
      isFiltered={!!search}
      onRetry={() => refetch()}
      emptyMessage={isPlatform ? <Trans>Platform actions will appear here.</Trans> : undefined}
    />
  )
}

export const Route = createFileRoute("/_auth/audit")({
  component: AuditLogPage,
})
